import { Component, ElementRef, inject, OnDestroy, OnInit, signal, Type, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { IconComponentV2 } from '../icon-component/icon.component';

export interface MobileSheetData {
  title: string;
  component: Type<any>;
  data?: Record<string, any>;
  icon?: string;
  showClose?: boolean;
  isFullHeight?: boolean;
  subtitle?: string;
}

@Component({
  selector: 'app-mobile-sheet',
  standalone: true,
  imports: [CommonModule, NgComponentOutlet, IconComponentV2],
  template: ` 
    <div class="mobile-sheet-content-wrapper bg-white dark:bg-surface-1 overflow-hidden flex flex-col shadow-2xl pb-3 transition-transform duration-300 [contain:layout_style_paint] [transform:translateZ(0)] will-change-transform"
      [class.rounded-t-[32px]]="!data.isFullHeight"
      [class.h-screen]="data.isFullHeight"
      [class.max-h-screen]="data.isFullHeight"
      [class.max-h-[85dvh]]="!data.isFullHeight"
      [style.transform]="translateY">
      <!-- Drag Area -->
      <div class="touch-none"
        (touchstart)="onTouchStart($event)"
        (touchmove)="onTouchMove($event)"
        (touchend)="onTouchEnd()">
        <!-- Drag Handle -->
        <div class="w-full flex justify-center py-3 cursor-grab active:cursor-grabbing">
          <div class="w-12 h-1.5 rounded-full bg-gray-200 dark:bg-line"></div>
        </div>
 
        <div class="px-6 pb-4 border-b border-gray-100 dark:border-line flex items-center justify-between bg-white dark:bg-surface-1 sticky top-0 z-10">
          <div class="flex items-center gap-3">
            @if (data.icon) {
              <app-icon-v2 [name]="data.icon" [size]="20" class="text-purple-600 dark:text-purple-300"></app-icon-v2>
            }
            <div>
              <h2 class="text-lg font-bold text-[#141437] dark:text-white leading-tight">{{ data.title }}</h2>
              @if (data.subtitle) {
                <p class="text-[10px] font-bold text-purple-600 dark:text-purple-300 uppercase tracking-widest mt-0.5">{{ data.subtitle }}</p>
              }
            </div>
          </div>
          
          @if (data.showClose !== false) {
            <button (click)="close()" class="p-2 rounded-xl bg-gray-50 dark:bg-surface-1 text-gray-400 dark:text-ink-soft hover:text-gray-600 dark:hover:text-white transition-colors">
              <app-icon-v2 name="x" [size]="20"></app-icon-v2>
            </button>
          }
        </div>
      </div>

      <!-- Content Area -->
     
      <div class="flex-1 overflow-y-auto no-scrollbar">
        @if (showContent()) {
          <ng-container *ngComponentOutlet="data.component; inputs: data.data"></ng-container>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class MobileSheetComponent implements OnInit, OnDestroy {
  public data = inject<MobileSheetData>(MAT_BOTTOM_SHEET_DATA);
  private bottomSheetRef = inject(MatBottomSheetRef<MobileSheetComponent>);
  private document = inject(DOCUMENT);
  private hostRef = inject(ElementRef<HTMLElement>);

  private startY = 0;
  private currentY = 0;
  public translateY = 'translateY(0px)'; 
  public showContent = signal(false);
 
  private deferredLockRaf: number | null = null; 
  private hasAppliedLock = false;

  // Track active sheets so nested/multiple sheets share a single scroll lock.
  private static activeSheets = 0;
  private static savedScrollY = 0;
  private static lockedScrollContainers: Array<{
    el: HTMLElement;
    overflow: string;
    overflowY: string;
    scrollTop: number;
  }> = [];
  private static savedBodyStyles: {
    position: string;
    top: string;
    left: string;
    right: string;
    width: string;
    overflow: string;
  } | null = null;
  private static savedHtmlStyles: {
    overflow: string;
    overscrollBehavior: string;
    touchAction: string;
  } | null = null;
  private static touchMoveHandler:
    | ((e: TouchEvent) => void)
    | null = null;
  private static activeSheetHosts: Set<HTMLElement> = new Set(); 
  private static cachedIsIOS: boolean | null = null;
  private static isIOS(): boolean {
    if (this.cachedIsIOS !== null) return this.cachedIsIOS;
    if (typeof navigator === 'undefined') {
      this.cachedIsIOS = false;
      return false;
    }
    const ua = navigator.userAgent || '';
    const isiOSDevice = /iPad|iPhone|iPod/.test(ua);
    const isiPadOS =
      navigator.platform === 'MacIntel' &&
      typeof (navigator as any).maxTouchPoints === 'number' &&
      (navigator as any).maxTouchPoints > 1;
    this.cachedIsIOS = isiOSDevice || isiPadOS;
    return this.cachedIsIOS;
  }

  ngOnInit(): void { 
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      this.deferredLockRaf = window.requestAnimationFrame(() => {
        this.deferredLockRaf = window.requestAnimationFrame(() => {
          this.deferredLockRaf = null;
          this.showContent.set(true);
          this.lockBodyScroll();
          this.hasAppliedLock = true;
        });
      });
    } else {
      this.showContent.set(true);
      this.lockBodyScroll();
      this.hasAppliedLock = true;
    }
  }

  ngOnDestroy(): void { 
    if (this.deferredLockRaf !== null) {
      cancelAnimationFrame(this.deferredLockRaf);
      this.deferredLockRaf = null;
      return;
    }
    if (this.hasAppliedLock) {
      this.unlockBodyScroll();
      this.hasAppliedLock = false;
    }
  }

  private lockBodyScroll(): void {
    const body = this.document.body;
    const html = this.document.documentElement;
    if (!body || !html) return;

    // Track this sheet's host so touchmove can identify legitimate scrolls inside any open sheet.
    const hostEl = this.hostRef?.nativeElement as HTMLElement | undefined;
    if (hostEl) {
      MobileSheetComponent.activeSheetHosts.add(hostEl);
    }

    MobileSheetComponent.activeSheets++;
    if (MobileSheetComponent.activeSheets > 1) return;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    MobileSheetComponent.savedScrollY = scrollY;
    MobileSheetComponent.savedBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    MobileSheetComponent.savedHtmlStyles = {
      overflow: html.style.overflow,
      overscrollBehavior: html.style.overscrollBehavior,
      touchAction: html.style.touchAction,
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    html.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    html.style.touchAction = 'none'; 
    MobileSheetComponent.lockedScrollContainers = [];
    if (!MobileSheetComponent.isIOS()) {
      const scopeRoot =
        (this.document.getElementById('private_layout') as HTMLElement | null) ||
        this.document.body;
      const overlayContainer = this.document.querySelector('.cdk-overlay-container');
      const candidates = scopeRoot.querySelectorAll<HTMLElement>(
        '.overflow-y-auto, .overflow-auto, .overflow-y-scroll'
      );
      candidates.forEach((el) => {
        // Skip elements inside the bottom-sheet overlay so the sheet's own scroll still works.
        if (overlayContainer && overlayContainer.contains(el)) return;
        MobileSheetComponent.lockedScrollContainers.push({
          el,
          overflow: el.style.overflow,
          overflowY: el.style.overflowY,
          scrollTop: el.scrollTop,
        });
        el.style.overflow = 'hidden';
        el.style.overflowY = 'hidden';
      });
    }

    // iOS Safari fallback: prevent any touchmove that doesn't originate from inside the sheet.
    MobileSheetComponent.touchMoveHandler = (e: TouchEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      for (const host of MobileSheetComponent.activeSheetHosts) {
        if (host.contains(target)) {
          return; // allow native scroll inside the sheet
        }
      }
      if (e.cancelable) {
        e.preventDefault();
      }
    };
    this.document.addEventListener(
      'touchmove',
      MobileSheetComponent.touchMoveHandler,
      { passive: false, capture: true }
    );
  }

  private unlockBodyScroll(): void {
    const body = this.document.body;
    const html = this.document.documentElement;
    if (!body || !html) return;

    const hostEl = this.hostRef?.nativeElement as HTMLElement | undefined;
    if (hostEl) {
      MobileSheetComponent.activeSheetHosts.delete(hostEl);
    }

    if (MobileSheetComponent.activeSheets > 0) {
      MobileSheetComponent.activeSheets--;
    }
    if (MobileSheetComponent.activeSheets > 0) return;

    const saved = MobileSheetComponent.savedBodyStyles;
    if (saved) {
      body.style.position = saved.position;
      body.style.top = saved.top;
      body.style.left = saved.left;
      body.style.right = saved.right;
      body.style.width = saved.width;
      body.style.overflow = saved.overflow;
      MobileSheetComponent.savedBodyStyles = null;
    } else {
      body.style.removeProperty('position');
      body.style.removeProperty('top');
      body.style.removeProperty('left');
      body.style.removeProperty('right');
      body.style.removeProperty('width');
      body.style.removeProperty('overflow');
    }

    const savedHtml = MobileSheetComponent.savedHtmlStyles;
    if (savedHtml) {
      html.style.overflow = savedHtml.overflow;
      html.style.overscrollBehavior = savedHtml.overscrollBehavior;
      html.style.touchAction = savedHtml.touchAction;
      MobileSheetComponent.savedHtmlStyles = null;
    } else {
      html.style.removeProperty('overflow');
      html.style.removeProperty('overscroll-behavior');
      html.style.removeProperty('touch-action');
    }

    // Restore inner scroll containers.
    MobileSheetComponent.lockedScrollContainers.forEach((entry) => {
      entry.el.style.overflow = entry.overflow;
      entry.el.style.overflowY = entry.overflowY;
      entry.el.scrollTop = entry.scrollTop;
    });
    MobileSheetComponent.lockedScrollContainers = [];

    if (MobileSheetComponent.touchMoveHandler) {
      this.document.removeEventListener(
        'touchmove',
        MobileSheetComponent.touchMoveHandler,
        { capture: true } as EventListenerOptions
      );
      MobileSheetComponent.touchMoveHandler = null;
    }

    window.scrollTo(0, MobileSheetComponent.savedScrollY);
  }

  onTouchStart(event: TouchEvent) {
    this.startY = event.touches[0].clientY;
  }

  onTouchMove(event: TouchEvent) {
    this.currentY = event.touches[0].clientY;
    const deltaY = this.currentY - this.startY;
    
    // Only allow downward drag
    if (deltaY > 0) {
      this.translateY = `translateY(${deltaY}px)`;
    }
  }

  onTouchEnd() {
    const deltaY = this.currentY - this.startY;
    if (deltaY > 100) {
      this.close();
    } else {
      // Snap back if threshold not met
      this.translateY = 'translateY(0px)';
    }
    // Reset tracking
    this.startY = 0;
    this.currentY = 0;
  }

  close() {
    this.bottomSheetRef.dismiss();
  }
}
